import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import AnnouncementDetailModal from "../../../components/AnnouncementDetailModal";
import { getCookie } from "../../../jwt/_helpers";
import {
  ANNOUNCEMENTS_CHANGED_EVENT,
  dismissAllAnnouncements,
  dismissAnnouncement,
  getAnnouncementAuditMetadata,
  getAnnouncementId,
  getAnnouncementsOverview,
  markAnnouncementRead,
} from "../../../utils/announcements";
import { sendAuditEvent } from "../../../utils/auditTrail";

const badgeStyle = {
  position: "absolute",
  top: "15px",
  right: "-10px",
  transform: "translateY(-50%)",
  cursor: "pointer",
};

const bellToggleFocusStyle = `
  .announcement-dropdown-toggle:focus,
  .announcement-dropdown-toggle:active,
  .announcement-dropdown-toggle.dropdown-toggle:focus,
  .announcement-dropdown-toggle.dropdown-toggle:active {
    outline: none !important;
    box-shadow: none !important;
  }
`;

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);

  const token = getCookie("token");

  const loadAnnouncements = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getAnnouncementsOverview(token);
      setAnnouncements(data.results || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch announcements", error);
    }
  }, [token]);

  useEffect(() => {
    loadAnnouncements();
    if (!token) return undefined;
    const intervalId = setInterval(loadAnnouncements, 60000);
    return () => clearInterval(intervalId);
  }, [loadAnnouncements, token]);

  useEffect(() => {
    const onAnnouncementsChanged = () => {
      loadAnnouncements();
    };
    window.addEventListener(ANNOUNCEMENTS_CHANGED_EVENT, onAnnouncementsChanged);
    return () => window.removeEventListener(ANNOUNCEMENTS_CHANGED_EVENT, onAnnouncementsChanged);
  }, [loadAnnouncements]);

  const visibleAnnouncements = useMemo(() => announcements.slice(0, 8), [announcements]);

  const onAnnouncementClick = async announcement => {
    if (!token) return;
    const announcementId = getAnnouncementId(announcement);
    const nextSelectedAnnouncement = announcement.is_read
      ? announcement
      : { ...announcement, is_read: true };
    setSelectedAnnouncement(nextSelectedAnnouncement);
    setIsAnnouncementModalOpen(true);

    if (!announcement.is_read) {
      setAnnouncements(prev =>
        prev.map(item =>
          (announcementId
            ? getAnnouncementId(item) === announcementId
            : item === announcement)
            ? { ...item, is_read: true }
            : item
        )
      );
      setUnreadCount(prev => Math.max(prev - 1, 0));

      if (!announcementId) {
        console.warn("Announcement id is missing", announcement);
        setSelectedAnnouncement(nextSelectedAnnouncement);
        setIsAnnouncementModalOpen(true);
        return;
      }

      try {
        await markAnnouncementRead(token, announcementId);
        sendAuditEvent({
          token,
          eventType: 10,
          route: typeof window !== "undefined" ? window.location.pathname : null,
          entityIds: [announcementId],
          metadata: getAnnouncementAuditMetadata(announcement, "read"),
        });
      } catch (error) {
        console.error("Failed to mark announcement as read", error);
        await loadAnnouncements();
      }
    }
  };

  const onDismissClick = async (event, announcement) => {
    event.preventDefault();
    event.stopPropagation();
    const announcementId = getAnnouncementId(announcement);
    setAnnouncements(prev =>
      prev.filter(item =>
        announcementId ? getAnnouncementId(item) !== announcementId : item !== announcement
      )
    );
    if (!announcement.is_read) {
      setUnreadCount(prev => Math.max(prev - 1, 0));
    }

    if (!token || !announcementId) {
      if (!announcementId) {
        console.warn("Announcement id is missing", announcement);
      }
      return;
    }

    try {
      await dismissAnnouncement(token, announcementId);
      sendAuditEvent({
        token,
        eventType: 11,
        route: typeof window !== "undefined" ? window.location.pathname : null,
        entityIds: [announcementId],
        metadata: getAnnouncementAuditMetadata(announcement, "dismiss"),
      });
    } catch (error) {
      console.error("Failed to dismiss announcement", error);
      await loadAnnouncements();
    }
  };

  const onDismissAllClick = async event => {
    event.preventDefault();
    event.stopPropagation();
    if (!token) return;
    if (announcements.length === 0) return;

    const previousAnnouncements = announcements;
    const entityIds = previousAnnouncements.map(getAnnouncementId).filter(Boolean);
    setAnnouncements([]);
    setUnreadCount(0);

    try {
      await dismissAllAnnouncements(token);
      sendAuditEvent({
        token,
        eventType: 11,
        route: typeof window !== "undefined" ? window.location.pathname : null,
        entityIds,
        metadata: {
          entity_type: "announcement",
          action: "dismiss_all",
          count: previousAnnouncements.length,
        },
      });
    } catch (error) {
      console.error("Failed to dismiss all announcements", error);
      await loadAnnouncements();
    }
  };

  const closeAnnouncementModal = () => {
    setIsAnnouncementModalOpen(false);
    setSelectedAnnouncement(null);
  };

  const bellTopOffset = unreadCount > 0 ? "9px" : "6px";

  return (
    <UncontrolledDropdown nav inNavbar style={{cursor: "pointer !important"}}>
      <style>{bellToggleFocusStyle}</style>
      <DropdownToggle
        nav
        caret
        tag="button"
        type="button"
        className="announcement-dropdown-toggle"
        style={{ cursor: "pointer", background: "transparent", border: 0, padding: 0 }}
      >
        <span
          style={{
            position: "relative",
            display: "inline-block",
            top: bellTopOffset,
            marginRight: "20px",
            cursor: "pointer",
          }}
        >
          <i className="mdi mdi-bell font-24" style={{ cursor: "pointer" }} />
          {unreadCount > 0 && (
            <Badge color="danger" pill style={badgeStyle}>
              {unreadCount}
            </Badge>
          )}
        </span>
      </DropdownToggle>
      <DropdownMenu
        right
        className="mailbox"
        style={{
          borderRadius: "10px",
          overflow: "hidden",
          padding: "4px",
        }}
      >
        <div className="d-flex no-block align-items-center justify-content-between p-3 bg-primary text-white mb-2">
          <div className="font-weight-bold">
            {unreadCount} Unread Announcements
          </div>
          <button
            type="button"
            disabled={announcements.length === 0}
            className="text-white font-weight-normal bg-transparent border-0 p-0"
            style={{
              whiteSpace: "nowrap",
              textDecoration: announcements.length === 0 ? "none" : "underline",
              cursor: announcements.length === 0 ? "not-allowed" : "pointer",
              opacity: announcements.length === 0 ? 0.5 : 1,
            }}
            onClick={onDismissAllClick}
          >
            Dismiss All
          </button>
        </div>
        <PerfectScrollbar
          style={{
            maxHeight: "420px",
            minHeight: 0,
          }}
        >
          {visibleAnnouncements.length === 0 && (
            <DropdownItem disabled>No announcements</DropdownItem>
          )}
          {visibleAnnouncements.map(announcement => (
            <DropdownItem
              key={getAnnouncementId(announcement) || announcement.title}
              tag="div"
              toggle={false}
              className="d-flex justify-content-between align-items-start"
              style={{
                backgroundColor: announcement.is_read ? "#ffffff" : "#e8f0ff",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                marginBottom: "8px",
              }}
            >
              <div
                style={{ maxWidth: "280px", cursor: "pointer" }}
                onClick={() => onAnnouncementClick(announcement)}
              >
                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                  <strong
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {announcement.title}
                  </strong>
                  {!announcement.is_read && (
                    <Badge
                      color={"primary"}
                      pill
                      className="ml-2"
                      style={{ flexShrink: 0 }}
                    >
                      New
                    </Badge>
                  )}
                </div>
              </div>
              <span
                className="text-muted ml-3"
                role="button"
                tabIndex={0}
                style={{ lineHeight: 1, cursor: "pointer", userSelect: "none", padding: "2px 4px" }}
                onClick={event => onDismissClick(event, announcement)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    onDismissClick(event, announcement);
                  }
                }}
              >
                x
              </span>
            </DropdownItem>
          ))}
        </PerfectScrollbar>
      </DropdownMenu>
      <AnnouncementDetailModal
        isOpen={isAnnouncementModalOpen}
        toggle={closeAnnouncementModal}
        announcement={selectedAnnouncement}
      />
    </UncontrolledDropdown>
  );
};

export default Announcements;
